// src/pages/notifications/TemplateDetailPage.tsx
// Template detail view with subject/body preview.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsTemplate, useNotifications, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Alert, Badge, ErrorAlert,
} from '@react/components';
import { formatChannelType, formatCategory } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { NotificationTemplate } from '@core/types/notifications/NotificationTemplate';

export const TemplateDetailPage = observer(() => {
  const store = useNotificationsTemplate();
  const notificationsStore = useNotifications();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) store.GetTemplate(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [showSendTest, setShowSendTest] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const tpl = store.currentItem as NotificationTemplate | null;
  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!tpl) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const chType = formatChannelType(tpl.channel_type ?? '');
  const category = formatCategory(tpl.category ?? '');

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const resp = await store.RenderTemplate(tpl.entity?.id ?? '', { variables: tpl.default_variables ?? {} } as never);
      const r = resp as { rendered_subject?: string; rendered_body?: string } | null;
      setPreview({ subject: r?.rendered_subject ?? '', body: r?.rendered_body ?? '' });
    } catch {
      // handled by store
    } finally {
      setPreviewing(false);
    }
  };

  // Send this template to one recipient via SendNotificationSmart —
  // "smart" routing picks the channel/priority per the recipient's own
  // preferences rather than the fixed channel_type on the template, which
  // is exactly what a "does this template actually reach someone" test
  // should exercise.
  const handleSendTest = async () => {
    if (!tpl.entity?.id || !testRecipient.trim()) return;
    setIsSendingTest(true);
    setTestResult(null);
    try {
      const resp = await notificationsStore.SendNotificationSmart({
        notification_type: tpl.event_type || tpl.name,
        target_entity: { type_id: '', type_name: 'profile', id: testRecipient.trim(), platform_id: '', tenant_id: '', product_id: '' },
        template_id: tpl.entity.id,
        template_variables: tpl.default_variables ?? {},
        force_send: true,
      } as never);
      const r = resp as { successful_count?: number; failed_count?: number } | null;
      setTestResult({
        ok: (r?.successful_count ?? 0) > 0,
        message: t('notifications.sendTest.result', { success: r?.successful_count ?? 0, failed: r?.failed_count ?? 0 }),
      });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : t('errors.unknown') });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('notifications.deleteTemplateConfirm'))) return;
    await store.DeleteTemplate(tpl.entity?.id ?? '');
    navigate('/notifications/templates');
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/notifications/templates" className="text-sm text-primary hover:text-primary/80">{t('notifications.templates')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{tpl.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handlePreview} isLoading={previewing}>{t('notifications.renderPreview')}</Button>
          <Button variant="secondary" onClick={() => setShowSendTest(v => !v)}>{t('notifications.sendTest.action')}</Button>
          <Link to={`/notifications/templates/${tpl.entity?.id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {showSendTest && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.sendTest.action')}</CardTitle></CardHeader>
          <CardContent className="flex items-end gap-2">
            <Input
              label={t('notifications.sendTest.recipient')}
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={handleSendTest} isLoading={isSendingTest} disabled={!testRecipient.trim()}>
              {t('notifications.sendTest.send')}
            </Button>
          </CardContent>
          {testResult && (
            <p className={`px-6 pb-4 text-sm ${testResult.ok ? 'text-success' : 'text-destructive'}`}>{testResult.message}</p>
          )}
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>{t('notifications.templateDetails')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={tpl.entity?.id} mono />
            <Field label={t('common.name')} value={tpl.name} />
            <Field label={t('common.description')} value={tpl.description} />
            <Field label={t('notifications.channelType')} value={<span className="inline-flex items-center gap-1.5"><NotificationIcon name={chType.icon} className="h-4 w-4" />{t(chType.labelKey)}</span>} />
            <Field label={t('notifications.category')} value={t(category.labelKey)} />
            <Field label={t('notifications.eventType')} value={tpl.event_type} />
            <Field label={t('notifications.language')} value={tpl.language} />
            <Field label={t('notifications.fallbackLanguage')} value={tpl.fallback_language} />
            <Field label={t('notifications.active')} value={<Badge variant={tpl.is_active ? 'success' : 'secondary'}>{tpl.is_active ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('notifications.default')} value={<Badge variant={tpl.is_default ? 'primary' : 'secondary'}>{tpl.is_default ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('notifications.version')} value={`v${tpl.version}`} />
            <Field label={t('notifications.requiredVariables')} value={(tpl.required_variables ?? []).join(', ')} mono />
            <Field label={t('common.createdAt')} value={tpl.created_at} />
            <Field label={t('common.updatedAt')} value={tpl.updated_at} />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t('notifications.subjectTemplate')}</CardTitle></CardHeader>
        <CardContent>
          <pre className="p-3 bg-muted rounded-lg font-mono text-sm whitespace-pre-wrap">{tpl.subject_template || t('common.notSet')}</pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t('notifications.bodyTemplate')}</CardTitle></CardHeader>
        <CardContent>
          <pre className="p-3 bg-muted rounded-lg font-mono text-sm whitespace-pre-wrap">{tpl.body_template || t('common.notSet')}</pre>
        </CardContent>
      </Card>

      {tpl.preview_text && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.previewText')}</CardTitle></CardHeader>
          <CardContent>
            <pre className="p-3 bg-muted rounded-lg font-mono text-sm whitespace-pre-wrap">{tpl.preview_text}</pre>
          </CardContent>
        </Card>
      )}

      {tpl.default_variables && Object.keys(tpl.default_variables).length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.defaultVariables')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(tpl.default_variables, null, 2)}</pre></CardContent>
        </Card>
      )}

      {preview && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.renderedPreview')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">{t('notifications.subject')}</p>
              <div className="p-3 bg-muted rounded-lg text-sm">{preview.subject}</div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">{t('notifications.bodyTemplate')}</p>
              <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: preview.body }} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default TemplateDetailPage;
